<template>
  <div id="app">
    <TimedLoadingScreen
      v-if="!isReadyState && !hasLoginError"
      :origin-page="$route.path"
      :time-interval="3000"
      message="Logging in.."
      :error-page="ROUTE_ERROR"
    />
    <router-view v-else />
    <shared-device-inactivity-tracker
      :user-name="displayName"
      :user-avatar="userImage"
      :can-use-shared-device="canUseSharedDevice"
      :must-use-shared-device="mustUseSharedDevice"
      :locale="$locale"
      @onUserLogout="onLogout"
      @onSDError="onSDError"
    />

    <!-- multiple: stacked modals render together, later portals on top -->
    <portal-target name="modal-target" multiple></portal-target>
  </div>
</template>

<script>
import {
  ROUTE_ERROR,
  ROUTE_NOT_FOUND,
  ROUTE_ROOT,
  ROUTE_UNAUTHORIZED,
} from '@/router/routes/routeNames'
import ApiService from '@/service/api.service.js'
import {
  redirectToLogin,
  revokeAndRedirectToLogin,
} from '@/service/loginActions'
import * as Sentry from '@sentry/browser'
import { PortalTarget } from 'portal-vue'
import { defineAbilitiesForRoleAndRules } from './ability/roles'
import TimedLoadingScreen from './components/generics/TimedLoadingScreen.vue'
import { generateRolesPerFlags, getUniqueFlags } from './helpers'
import { checkAccessForNavigation } from './helpers/routeAccessValidation'
import { useCurrentCompanySettings } from './hooks/swr/useCurrentCompany'
import { useFeatureFlag } from './hooks/useFeatureFlags'
import { loadLocales } from './i18n/lib/i18n'

export default {
  name: 'App',
  components: {
    TimedLoadingScreen,
    PortalTarget,
  },
  provide() {
    return {
      store: this.$store,
      userPermission: this.$userPermission,
    }
  },
  setup() {
    const { isEnabled: canUseSharedDevice } = useFeatureFlag(
      'can_use_shared_device'
    )
    const { isEnabled: mustUseSharedDevice } = useFeatureFlag(
      'must_use_shared_device'
    )

    const { companySettings } = useCurrentCompanySettings()

    return { canUseSharedDevice, mustUseSharedDevice, companySettings }
  },
  data() {
    return {
      ROUTE_ERROR,
      isLoggedIn: false,
      hasLoginError: false,
      userImage: '',
      displayName: '',
      isPermissionsSet: false,
      pendingRoute: this.$route.path,
    }
  },
  computed: {
    allResourcesLoaded() {
      return this.$store.state.common.allResourcesLoaded
    },
    currentUser() {
      return this.$store.state.common.currentUser
    },
    currentCompany() {
      return this.$store.state.common.currentCompany
    },
    isReadyState() {
      return (
        this.isAllowedCompany &&
        this.isPermissionsSet &&
        this.allResourcesLoaded
      )
    },
    isAllowedCompany() {
      const currentSubdomain = this.$router.options.base
      const companies = this.$store.state.common.userCompanies
      const isAllowed = companies
        .map((company) => company.subdomain)
        .includes(currentSubdomain)
      return isAllowed
    },
  },
  watch: {
    allResourcesLoaded(isLoaded) {
      if (isLoaded) {
        this.checkAllowedCompany()
        this.checkDepartment()
        this.setUserAccess()
        this.setUserLocale()
      }
    },
    companySettings: {
      handler() {
        if (this.companySettings) {
          this.$userPermission.setCompanySettingsData(this.companySettings)
        }
      },
      deep: true,
      immediate: true,
    },
  },
  created() {
    this.checkLogin()
      .then((isLoggedIn) => {
        // 401 is already redirecting to login; a non-401 failure already routed to
        // the error screen. Either way, stop — don't fire the authenticated
        // bootstrap requests behind it.
        if (!isLoggedIn) {
          return
        }

        // Recovered on a reload that landed on /error: move back to the app.
        if (this.$route.name === ROUTE_ERROR) {
          this.$router.push({ name: ROUTE_ROOT })
        }

        // hide access token (api_token) from URL
        if (this.$route.query.api_token !== undefined) {
          this.$router.push(this.$route.path)
        }
        this.setDocumentTitle()
        // Returned so a rejection from its sub-fetches reaches .catch below.
        return this.$store.dispatch('initializeAppData')
      })
      .catch(() => this.goToErrorPage())
  },
  methods: {
    setDocumentTitle() {
      document.title = 'Attensi Manager'
    },
    async checkLogin() {
      const response = await ApiService.getCurrentUser()
      // check if token is valid
      if (response.status === 401) {
        await redirectToLogin()
        return false
      }

      // A non-401 failure (offline, 5xx, auth outage) resolves as
      // { error: true, status, data } with no usable user — getCurrentUser never
      // throws. Surface the error screen now instead of reading response.data.id,
      // and report failure so the caller skips the authenticated bootstrap.
      if (response.error || response.data?.id == null) {
        await this.goToErrorPage()
        return false
      }

      Sentry.setUser({
        id: response.data.id.toString(),
      })
      this.userImage = response.data.image
      this.setDisplayName(response.data)
      this.isLoggedIn = true
      return true
    },
    async goToErrorPage() {
      // Navigate first so <router-view> renders /error rather than a data-less
      // root route in the tick between flipping the flag and the push resolving.
      if (this.$route.name !== ROUTE_ERROR) {
        await this.$router.push({ name: ROUTE_ERROR }).catch(() => {})
      }
      // Un-gates <router-view> (isReadyState never flips on a login failure, so
      // without this TimedLoadingScreen would mask /error).
      this.hasLoginError = true
    },
    onLogout() {
      this.isLoggedIn = false
      revokeAndRedirectToLogin()
    },
    onSDError(error) {
      Sentry.captureEvent(error)
    },
    setDisplayName(user) {
      this.displayName = user?.anonymous
        ? this.$t('ANON')
        : user?.username || user?.first_name
    },
    checkAllowedCompany() {
      if (!this.isAllowedCompany) {
        // remove cached subdomain
        window.localStorage.removeItem('subdomain')
        this.$router.push({ name: ROUTE_NOT_FOUND })
      }
    },
    setUserAccess() {
      const userRoles = this.currentUser.roles
      const featureFlags = [
        ...this.currentCompany.supported_configurations,
        ...this.currentUser.features.adminview,
        ...this.currentCompany.adminview_configurations,
      ]
      const uniqueFlags = getUniqueFlags(featureFlags)
      const featureRules = generateRolesPerFlags(uniqueFlags)
      const abilities = defineAbilitiesForRoleAndRules(userRoles, featureRules)
      this.$ability.update(abilities.rules)
      // supported_configurations describes platform capabilities, not on/off flags — omit it here
      this.$featureFlag.setEnabled([
        ...this.currentUser.features.adminview,
        ...this.currentCompany.adminview_configurations,
      ])
      this.$userPermission.setUserPermissionsData(
        this.currentUser.v3.accessibles
      )

      this.checkAccessForCurrentRoute()
      this.setNavigationGuard()
      this.isPermissionsSet = true
    },
    checkAccessForCurrentRoute() {
      const canView = checkAccessForNavigation(
        this.currentCompany,
        this.currentUser,
        this.$route,
        this.$ability
      )

      if (!canView) {
        this.$router.push({
          name: ROUTE_UNAUTHORIZED,
          params: { allowTakeMeHome: true },
        })
      }
    },
    setNavigationGuard() {
      // add navigation guard to the global $router object, this will be run on every route
      this.$router.beforeEach((to, from, next) => {
        const canNavigate = checkAccessForNavigation(
          this.currentCompany,
          this.currentUser,
          to,
          this.$ability
        )

        if (!canNavigate) {
          this.$toast({ text: this.$t('NO_ACCESS') }, { theme: 'error' })
          return next({ name: ROUTE_ROOT })
        }
        next()
      })
    },
    setUserLocale() {
      const userLocale = this.currentUser.v3.locale
      this.$locale = userLocale
      loadLocales({
        locale: userLocale,
      })
    },
    checkDepartment() {
      const hasEmptyDepartment =
        typeof this.currentUser.department === 'undefined' ||
        this.currentUser.department === null

      if (hasEmptyDepartment && !this.$store.getters.isDevUser) {
        this.$toast(
          {
            text: `Error : Configuration: User has no department selected.`,
          },
          {
            isSelfClosing: false,
            theme: 'error',
          }
        )
      }
    },
  },
}
</script>

<style src="../static/normalize.css"></style>
<style src="./styles/colors/systemColors.css"></style>
<style src="./styles/colors/themes.css"></style>

<style lang="scss">
body {
  font-family: 'Lato', sans-serif;
  background: $color-body;
  color: $color-text;
}

p {
  font-weight: 400;
  font-size: $font-size-m;
  margin-bottom: 16px;
  line-height: 24px;

  &:last-child {
    margin-bottom: 0;
  }
}

p.large {
  font-weight: 400;
  font-size: 18px;
  margin-bottom: 20px;
  line-height: 28px;
}

.disableScroll {
  overflow: hidden;
}

img {
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome and Opera */
  -webkit-tap-highlight-color: transparent;
  -webkit-user-drag: none;
}

a {
  color: $color-navigation;
  text-decoration: none;
  font-weight: 900;

  &:visited {
    color: $color-blue-60;
  }
}
</style>
